<template>
  <WTooltip
    :section-tag="sectionTag"
    :description="description"
    :position="position"
    :width="width"
    :size="size"
    :min-width="minWidth"
    :max-width="maxWidth"
    :open-delay="openDelay"
    :close-delay="closeDelay"
    :hover-open-delay="hoverOpenDelay"
    :focus-open-delay="focusOpenDelay"
    :hover-close-delay="hoverCloseDelay"
    :focus-close-delay="focusCloseDelay"
    :interactive="interactive"
    :transition="transition"
    :show-arrow="showArrow"
    intent="help"
  >
    <template #activator="{ props: activatorProps }">
      <button
        v-bind="activatorProps"
        :data-testid="sectionTag ? `${sectionTag}-trigger` : undefined"
        :aria-label="triggerAriaLabel"
        class="w-help-tooltip__trigger"
        type="button"
      >
        <slot name="icon">
          <IconInfo
            v-if="icon === 'info'"
            :section-tag="sectionTag ? `${sectionTag}-info-icon` : undefined"
            :size="iconSize"
          />
          <IconQuestion
            v-else
            :section-tag="sectionTag ? `${sectionTag}-question-icon` : undefined"
            :size="iconSize"
          />
        </slot>
      </button>
    </template>

    <template v-if="$slots.content" #content>
      <slot name="content" />
    </template>
  </WTooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { $t } from '@/plugins/i18n'
import { tokens } from '@/plugins/vuetify'
import { IconInfo, IconQuestion } from '@wasabi/patterns'
import WTooltip from './WTooltip.vue'
import type { WTooltipDelay, WTooltipDimension, WTooltipPosition, WTooltipSize, WTooltipTransition } from './WTooltip.vue'

export type WHelpTooltipIcon = 'question' | 'info'

export interface WHelpTooltipProps {
  sectionTag?: string
  description: string
  position?: WTooltipPosition
  width?: WTooltipDimension
  size?: WTooltipSize
  minWidth?: WTooltipDimension
  maxWidth?: WTooltipDimension
  ariaLabel?: string
  icon?: WHelpTooltipIcon
  iconSize?: string
  openDelay?: WTooltipDelay
  closeDelay?: WTooltipDelay
  hoverOpenDelay?: WTooltipDelay
  focusOpenDelay?: WTooltipDelay
  hoverCloseDelay?: WTooltipDelay
  focusCloseDelay?: WTooltipDelay
  interactive?: boolean
  transition?: WTooltipTransition
  showArrow?: boolean
}

const props = withDefaults(defineProps<WHelpTooltipProps>(), {
  sectionTag: 'unspecified',
  position: 'top',
  width: undefined,
  size: undefined,
  minWidth: undefined,
  maxWidth: undefined,
  ariaLabel: undefined,
  icon: 'question',
  iconSize: '14px',
  openDelay: undefined,
  closeDelay: undefined,
  hoverOpenDelay: undefined,
  focusOpenDelay: undefined,
  hoverCloseDelay: undefined,
  focusCloseDelay: undefined,
  interactive: undefined,
  transition: undefined,
  showArrow: undefined,
})

const triggerAriaLabel = computed(() => props.ariaLabel || $t('app.common.actions.showHelp'))
</script>

<style scoped lang="scss">
.w-help-tooltip__trigger {
  appearance: none;
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: v-bind('tokens.borderRadius.control.full');
  cursor: help;
  display: inline-flex;
  height: 18px;
  justify-content: center;
  line-height: 0;
  padding: 0;
  width: 18px;
}

.w-help-tooltip__trigger:focus-visible {
  box-shadow: 0 0 0 3px v-bind('tokens.color.focus.ring');
  outline: 2px solid v-bind('tokens.color.input.border.focus');
  outline-offset: 1px;
}
</style>
